import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import { ArrowBack, Cancel, Send, Done, Receipt } from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { api } from '../../services/apiClient';

// ---------------------------------------------------------------------------
// Delivery 360: header + lifecycle ribbon, delivered lines, and the
// Dispatch/Deliver/Cancel workflow actions. Mirrors RfqDetailPage.tsx's
// overall shape.
// ---------------------------------------------------------------------------

type DeliveryStatus = 'DRAFT' | 'DISPATCHED' | 'DELIVERED' | 'CANCELLED';

const FLOW_STEPS: { key: string; label: string }[] = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'DISPATCHED', label: 'Dispatched' },
  { key: 'DELIVERED', label: 'Delivered' },
];

const FLOW_INDEX: Partial<Record<DeliveryStatus, number>> = {
  DRAFT: 0,
  DISPATCHED: 1,
  DELIVERED: 2,
};

const STATUS_TONE: Record<DeliveryStatus, StatusTone> = {
  DRAFT: 'neutral',
  DISPATCHED: 'info',
  DELIVERED: 'success',
  CANCELLED: 'error',
};

interface DeliveryItem {
  id?: number;
  product_id?: string | number | null;
  product_name?: string | null;
  delivered_qty?: number | string | null;
  quantity?: number | string | null;
  uom?: string | null;
}

interface DeliveryDetail {
  id: number;
  delivery_number: string;
  sales_order_id: number | string;
  sales_order_number?: string | null;
  customer_id?: number | string;
  customer_name?: string;
  dispatch_date?: string | null;
  delivered_date?: string | null;
  transporter?: string | null;
  vehicle_no?: string | null;
  transport_mode?: string | null;
  dispatch_from?: string | null;
  deliver_to?: string | null;
  distance_km?: number | string | null;
  warehouse_name?: string | null;
  remarks?: string | null;
  status: DeliveryStatus;
  items: DeliveryItem[];
}

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

const DeliveryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [delivery, setDelivery] = useState<DeliveryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { delivery: data } = await api.get<{ delivery: DeliveryDetail }>(`/deliveries/${id}`);
      setDelivery(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load delivery');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !delivery) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/deliveries')} sx={{ mb: 2 }}>
          Back to Deliveries
        </Button>
        <Alert severity="error">{error || 'Delivery not found'}</Alert>
      </Box>
    );
  }

  const isSideState = delivery.status === 'CANCELLED';
  const flowIndex = FLOW_INDEX[delivery.status] ?? 0;

  const canDispatch = delivery.status === 'DRAFT';
  const canDeliver = delivery.status === 'DISPATCHED';
  const canCancel = delivery.status === 'DRAFT' || delivery.status === 'DISPATCHED';
  const noActionsAvailable = !canDispatch && !canDeliver && !canCancel;

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDispatch = () =>
    runAction('dispatch', async () => {
      await api.post(`/deliveries/${id}/dispatch`);
      setNotice('Delivery dispatched.');
      await load();
    });

  const handleDeliver = () =>
    runAction('deliver', async () => {
      await api.post(`/deliveries/${id}/deliver`);
      setNotice('Delivery marked as delivered.');
      await load();
    });

  const handleCancel = () =>
    runAction('cancel', async () => {
      await api.post(`/deliveries/${id}/cancel`, { reason: cancelReason.trim() || undefined });
      setCancelOpen(false);
      setCancelReason('');
      setNotice('Delivery cancelled.');
      await load();
    });

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/deliveries')} sx={{ mb: 1 }}>
        Back to Deliveries
      </Button>

      <PageHeader
        title={delivery.delivery_number}
        qualifier={delivery.customer_name ? `· ${delivery.customer_name}` : undefined}
        subtitle={delivery.dispatch_date ? `Dispatched ${fmtDate(delivery.dispatch_date)}` : 'Not yet dispatched'}
        actions={<StatusDot label={delivery.status} tone={STATUS_TONE[delivery.status]} />}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Lifecycle ribbon */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {isSideState ? (
          <>
            <Stepper activeStep={-1} alternativeLabel sx={{ opacity: 0.45, mb: 2 }}>
              {FLOW_STEPS.map((s) => (
                <Step key={s.key}>
                  <StepLabel>{s.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Alert severity="warning" icon={<Cancel />}>
              <Typography sx={{ fontWeight: 700 }}>Delivery Cancelled</Typography>
            </Alert>
          </>
        ) : (
          <Stepper activeStep={flowIndex} alternativeLabel>
            {FLOW_STEPS.map((s, i) => (
              <Step key={s.key} completed={i < flowIndex}>
                <StepLabel>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>

      {/* Workflow actions */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canDispatch && (
            <Button variant="contained" startIcon={<Send />} disabled={actionLoading !== null} onClick={handleDispatch}>
              {actionLoading === 'dispatch' ? 'Dispatching...' : 'Dispatch'}
            </Button>
          )}
          {canDeliver && (
            <Button variant="contained" color="success" startIcon={<Done />} disabled={actionLoading !== null} onClick={handleDeliver}>
              {actionLoading === 'deliver' ? 'Recording...' : 'Mark Delivered'}
            </Button>
          )}
          {canCancel && (
            <Button variant="outlined" color="error" startIcon={<Cancel />} disabled={actionLoading !== null} onClick={() => setCancelOpen(true)}>
              Cancel Delivery
            </Button>
          )}
          {(delivery.status === 'DISPATCHED' || delivery.status === 'DELIVERED') && (
            <Button variant="outlined" startIcon={<Receipt />} onClick={() => navigate(`/sales/invoices/new?deliveryId=${delivery.id}`)}>
              Create Invoice
            </Button>
          )}
          {noActionsAvailable && (
            <Typography variant="body2" color="text.secondary">
              This delivery is {delivery.status.toLowerCase()} -- no further actions are available.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Delivery Details
            </Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Sales Order
                </Typography>
                {delivery.sales_order_id ? (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => navigate(`/sales/orders/${delivery.sales_order_id}`)}
                  >
                    {delivery.sales_order_number || `SO #${delivery.sales_order_id}`}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>—</Typography>
                )}
              </Grid>
              {[
                ['Customer', delivery.customer_name],
                ['Transporter', delivery.transporter],
                ['Vehicle No', delivery.vehicle_no],
                ['Transport Mode', delivery.transport_mode],
                ['Warehouse', delivery.warehouse_name],
                ['Dispatch From', delivery.dispatch_from],
                ['Deliver To', delivery.deliver_to],
                ['Distance', delivery.distance_km !== null && delivery.distance_km !== undefined ? `${toNum(delivery.distance_km)} km` : null],
                ['Dispatch Date', fmtDate(delivery.dispatch_date)],
                ['Delivered Date', fmtDate(delivery.delivered_date)],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Remarks
            </Typography>
            <Typography variant="body2" color={delivery.remarks ? 'text.primary' : 'text.disabled'} sx={{ whiteSpace: 'pre-wrap' }}>
              {delivery.remarks || 'No remarks recorded.'}
            </Typography>
          </Paper>
        </Grid>

        {/* Items */}
        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Delivered Lines
              </Typography>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Product', 'Qty', 'UOM'].map((h, i) => (
                      <TableCell key={h} align={i === 1 ? 'right' : 'left'} sx={headerCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {delivery.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ border: 0 }}>
                        <TableEmptyState title="No line items on this delivery" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    delivery.items.map((item, idx) => (
                      <TableRow key={item.id ?? idx} sx={bodyRowSx}>
                        <TableCell sx={{ py: 1.25 }}>{item.product_name || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {toNum(item.delivered_qty ?? item.quantity)}
                        </TableCell>
                        <TableCell>{item.uom || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>
      </Grid>

      {/* Cancel confirm */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Delivery</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>This cannot be undone.</DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep It</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={actionLoading === 'cancel'}>
            {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Delivery'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryDetailPage;
